/**
 * ResetPanel -- Reset skills/attributes at the sage's sanctum.
 *
 * Reset skill points (100g x level) or attribute points (50g x level).
 * Includes confirmation dialog before reset.
 *
 * @module ResetPanel
 */

import focusManager from '../../input/FocusManager.js';
import townSystem from '../../systems/TownSystem.js';

export default class ResetPanel {
  constructor(options = {}) {
    this._party = options.party || null;
    this._townId = options.townId || null;
    this._onClose = options.onClose || null;
    this._el = null;
    this._isOpen = false;
    this._statusMsg = '';
    this._selectedCharIndex = 0;
    this._confirmAction = null; // { type, label, cost, charIndex }
  }

  get isOpen() { return this._isOpen; }

  open() {
    if (this._isOpen) return;
    const uiRoot = document.getElementById('ui-root');
    if (!uiRoot) return;

    this.close();
    this._isOpen = true;

    this._el = document.createElement('div');
    this._el.className = 'town-panel-overlay';
    this._el.id = 'reset-panel';
    this._el.setAttribute('role', 'dialog');
    this._el.setAttribute('aria-label', "Sage's Sanctum");
    this._el.setAttribute('data-scene', 'reset');

    this._render();
    uiRoot.appendChild(this._el);
    focusManager.pushGroup('reset', this._el);
  }

  close() {
    if (this._el && this._el.parentNode) {
      this._el.parentNode.removeChild(this._el);
      const topGroup = focusManager._currentGroup ? focusManager._currentGroup() : null;
      if (topGroup && topGroup.name === 'reset') {
        focusManager.popGroup();
      }
    }
    this._el = null;
    this._isOpen = false;
    this._statusMsg = '';
    this._confirmAction = null;
    if (this._onClose) this._onClose();
  }

  _render() {
    if (!this._el) return;
    this._el.innerHTML = '';

    const panel = document.createElement('div');
    panel.className = 'town-panel';

    // Header
    const header = document.createElement('div');
    header.className = 'town-panel-header';
    const title = document.createElement('h2');
    title.className = 'town-panel-title';
    title.textContent = "Sage's Sanctum";
    header.appendChild(title);

    const gold = document.createElement('span');
    gold.className = 'town-panel-gold';
    gold.textContent = `Gold: ${this._party?.gold || 0}`;
    header.appendChild(gold);

    const closeBtn = document.createElement('button');
    closeBtn.className = 'town-panel-close';
    closeBtn.setAttribute('data-focusable', 'true');
    closeBtn.textContent = 'X';
    closeBtn.addEventListener('click', () => this.close());
    header.appendChild(closeBtn);
    panel.appendChild(header);

    // Status
    if (this._statusMsg) {
      const status = document.createElement('div');
      status.className = 'town-panel-status';
      status.textContent = this._statusMsg;
      panel.appendChild(status);
    }

    const content = document.createElement('div');
    content.className = 'town-panel-content';

    if (this._confirmAction) {
      this._renderConfirmation(content);
    } else {
      this._renderCharacterList(content);
    }

    panel.appendChild(content);
    this._el.appendChild(panel);
  }

  _renderCharacterList(container) {
    const party = this._party;
    if (!party || party.members.length === 0) {
      const empty = document.createElement('div');
      empty.className = 'town-panel-empty';
      empty.textContent = 'No party members.';
      container.appendChild(empty);
      return;
    }

    for (let i = 0; i < party.members.length; i++) {
      const char = party.members[i];
      const skillCost = townSystem.getSkillResetCost(char);
      const attrCost = townSystem.getAttributeResetCost(char);

      const charCard = document.createElement('div');
      charCard.className = 'town-reset-card';

      const charName = document.createElement('div');
      charName.className = 'town-reset-name';
      charName.textContent = `${char.name} (Lvl ${char.level})`;
      charCard.appendChild(charName);

      // Skill reset button
      const skillBtn = document.createElement('button');
      skillBtn.className = 'town-panel-btn';
      skillBtn.setAttribute('data-focusable', 'true');
      skillBtn.textContent = `Reset Skills (${skillCost}g)`;
      if (party.gold >= skillCost) {
        skillBtn.addEventListener('click', () => {
          this._confirmAction = { type: 'skills', label: `Reset skills for ${char.name}?`, cost: skillCost, charIndex: i };
          this._render();
          focusManager.refreshGroup();
        });
      } else {
        skillBtn.classList.add('town-panel-btn--disabled');
      }
      charCard.appendChild(skillBtn);

      // Attribute reset button
      const attrBtn = document.createElement('button');
      attrBtn.className = 'town-panel-btn';
      attrBtn.setAttribute('data-focusable', 'true');
      attrBtn.textContent = `Reset Attributes (${attrCost}g)`;
      if (party.gold >= attrCost) {
        attrBtn.addEventListener('click', () => {
          this._confirmAction = { type: 'attributes', label: `Reset attributes for ${char.name}?`, cost: attrCost, charIndex: i };
          this._render();
          focusManager.refreshGroup();
        });
      } else {
        attrBtn.classList.add('town-panel-btn--disabled');
      }
      charCard.appendChild(attrBtn);

      container.appendChild(charCard);
    }
  }

  _renderConfirmation(container) {
    const action = this._confirmAction;

    const confirmBox = document.createElement('div');
    confirmBox.className = 'town-confirm-box';

    const msg = document.createElement('div');
    msg.className = 'town-confirm-msg';
    msg.textContent = `${action.label} This will cost ${action.cost}g.`;
    confirmBox.appendChild(msg);

    const btnRow = document.createElement('div');
    btnRow.className = 'town-confirm-btns';

    const yesBtn = document.createElement('button');
    yesBtn.className = 'town-panel-btn town-confirm-yes';
    yesBtn.setAttribute('data-focusable', 'true');
    yesBtn.textContent = 'Confirm';
    yesBtn.addEventListener('click', () => {
      const char = this._party.members[action.charIndex];
      if (!char) {
        this._statusMsg = 'Character not found.';
      } else if (action.type === 'skills') {
        const result = townSystem.resetSkills(this._party, char);
        this._statusMsg = result.message;
      } else if (action.type === 'attributes') {
        const result = townSystem.resetAttributes(this._party, char);
        this._statusMsg = result.message;
      }
      this._confirmAction = null;
      this._render();
      focusManager.refreshGroup();
    });
    btnRow.appendChild(yesBtn);

    const noBtn = document.createElement('button');
    noBtn.className = 'town-panel-btn town-confirm-no';
    noBtn.setAttribute('data-focusable', 'true');
    noBtn.textContent = 'Cancel';
    noBtn.addEventListener('click', () => {
      this._confirmAction = null;
      this._render();
      focusManager.refreshGroup();
    });
    btnRow.appendChild(noBtn);

    confirmBox.appendChild(btnRow);
    container.appendChild(confirmBox);
  }
}
